import type { Question, QuestionType } from "./types";
import { el, shuffleArray } from "./utils";

// Component: one Bootstrap card per question, with the input built
// by the renderer that matches the question's type
export function QuestionCard(question: Question): HTMLElement {
  const card = el("div", "card mb-4 shadow-sm");
  const body = el("div", "card-body");

  const title = el("h5", "card-title");
  const mark = el("span");
  mark.id = `markImg${question.id}`;
  title.append(mark, `${question.id}. ${question.prompt}`);

  const feedback = el("div");
  feedback.id = `q${question.id}Feedback`;

  body.append(title, inputRenderers[question.type](question), feedback);
  card.appendChild(body);
  return card;
}

// One renderer per input type
const inputRenderers: Record<QuestionType, (question: Question) => HTMLElement> = {
  text(question) {
    const input = el("input", "form-control");
    input.type = "text";
    input.id = `q${question.id}`;
    input.placeholder = "Type your answer";
    return input;
  },

  number(question) {
    const input = el("input", "form-control");
    input.type = "number";
    input.id = `q${question.id}`;
    input.min = String(question.min ?? "");
    input.max = String(question.max ?? "");
    input.placeholder = "Enter a number";
    return input;
  },

  select(question) {
    const select = el("select", "form-select");
    select.id = `q${question.id}`;

    const placeholder = el("option");
    placeholder.value = "";
    placeholder.textContent = "Select One";
    select.appendChild(placeholder);

    for (const choice of question.choices ?? []) {
      const option = el("option");
      option.value = choice;
      option.textContent = choice;
      select.appendChild(option);
    }
    return select;
  },

  radio(question) {
    return ChoiceGroup(question, "radio");
  },

  checkbox(question) {
    return ChoiceGroup(question, "checkbox");
  },

  range(question) {
    const wrapper = el("div");

    const label = el("label", "form-label");
    label.htmlFor = `q${question.id}`;
    const value = el("strong");
    value.id = `q${question.id}Value`;
    value.textContent = String(question.min ?? "");
    label.append("Slide to your answer: ", value);

    const input = el("input", "form-range");
    input.type = "range";
    input.id = `q${question.id}`;
    input.min = String(question.min ?? "");
    input.max = String(question.max ?? "");
    input.value = input.min;
    input.addEventListener("input", function () {
      value.textContent = this.value;
    });

    wrapper.append(label, input);
    return wrapper;
  },

  datalist(question) {
    const wrapper = el("div");

    const input = el("input", "form-control");
    input.type = "text";
    input.id = `q${question.id}`;
    input.setAttribute("list", `q${question.id}List`);
    input.placeholder = "Start typing an answer...";

    const list = el("datalist");
    list.id = `q${question.id}List`;
    for (const choice of question.choices ?? []) {
      const option = el("option");
      option.value = choice;
      list.appendChild(option);
    }

    wrapper.append(input, list);
    return wrapper;
  },
};

// Component: a group of labeled radio buttons or checkboxes,
// shuffled when the question asks for random order
function ChoiceGroup(question: Question, type: "radio" | "checkbox"): HTMLElement {
  const container = el("div");
  const choices = [...(question.choices ?? [])];

  if (question.shuffle) {
    shuffleArray(choices);
  }

  for (const choice of choices) {
    const wrapper = el("div", "form-check form-check-inline");

    const input = el("input", "form-check-input");
    input.type = type;
    input.name = `q${question.id}`;
    input.id = `q${question.id}-${choice.replace(/\W/g, "")}`;
    input.value = choice;

    const label = el("label", "form-check-label");
    label.htmlFor = input.id;
    label.textContent = choice;

    wrapper.append(input, label);
    container.appendChild(wrapper);
  }
  return container;
}
